// Regras de apresentação das telas de conta (`spec/tasks/WEB-61.md`, `spec/08` §4.1 e §4.3).
// Fora do JSX porque são as únicas partes testáveis sem render.

import type { VariantProps } from 'class-variance-authority';
import type { RateSettings, SellerProfile } from '../../api/queries/account.ts';
import type { badgeVariants } from '../../components/ui/badge.tsx';
import { formatCents, pctOf } from '../../lib/money.ts';

/** Tom do `Badge` do Zenith (WEB-100). Só o subconjunto que estas telas usam. */
export type StatusTone = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

/**
 * `bps` → percentual em pt-BR, por aritmética de inteiro. Sem `toFixed` e sem divisão em
 * float: `149 / 100` já é `1.49` com erro de ponto flutuante, e a mesma regra que vale
 * para dinheiro (`spec/17` §4.6) vale para a taxa que multiplica dinheiro.
 *
 * Continua aqui (e não no `pct` do kit, que escreve `1,49 %` com espaço) porque
 * `features/admin-rates` e o admin inteiro importam deste módulo: duas grafias de
 * percentual em telas vizinhas seria pior que o espaço a menos do croqui.
 */
export function formatBps(bps: number): string {
  const negative = bps < 0;
  const digits = String(Math.abs(Math.trunc(bps))).padStart(3, '0');
  const whole = Number(digits.slice(0, -2));
  const fraction = digits.slice(-2);
  return `${negative ? '−' : ''}${whole},${fraction}%`;
}

export const SELLER_STATUS: Record<SellerProfile['status'], { label: string; tone: StatusTone }> = {
  pending_kyc: { label: 'verificação pendente', tone: 'warning' },
  active: { label: 'ativa', tone: 'success' },
  suspended: { label: 'suspensa', tone: 'destructive' },
  closed: { label: 'encerrada', tone: 'secondary' },
};

export const KYC_STATUS: Record<SellerProfile['kyc_status'], { label: string; tone: StatusTone }> =
  {
    none: { label: 'não enviada', tone: 'secondary' },
    pending: { label: 'em análise', tone: 'warning' },
    approved: { label: 'aprovada', tone: 'success' },
    rejected: { label: 'reprovada', tone: 'destructive' },
  };

/** `null` é "sem limite", nunca `R$ 0,00`: a diferença é o saque inteiro passar ou não. */
export function dailyLimitLabel(cents: number | null): string {
  return cents === null ? 'sem limite' : formatCents(cents);
}

// ---------------------------------------------------------------------------
// Taxas: rótulos, diferença entre versões e o simulador
// ---------------------------------------------------------------------------

/**
 * Campos que descrevem uma tabela. `reserve_pct_bps`/`reserve_days` entram na lista
 * (`spec/24` §3: a reserva é real e vira o 4º `RateCard`); o croqui nem os declarava.
 */
export const RATE_KEYS = [
  'pix_pct_bps',
  'pix_fixed_cents',
  'withdraw_fee_cents',
  'daily_withdraw_limit_cents',
  'chargeback_fee_cents',
  'reserve_pct_bps',
  'reserve_days',
] as const;

export type RateKey = (typeof RATE_KEYS)[number];

/** Rótulo longo (drawer de comparação e `Notice` da mudança agendada). */
export const RATE_LABEL: Record<RateKey, string> = {
  pix_pct_bps: 'Pix · percentual',
  pix_fixed_cents: 'Pix · fixo',
  withdraw_fee_cents: 'Taxa de saque',
  daily_withdraw_limit_cents: 'Limite diário de saque',
  chargeback_fee_cents: 'Taxa de chargeback',
  reserve_pct_bps: 'Reserva · percentual',
  reserve_days: 'Reserva · retenção',
};

/** Rótulo curto (`Badge` da tabela de mudanças agendadas). */
export const RATE_SHORT: Record<RateKey, string> = {
  pix_pct_bps: 'Pix',
  pix_fixed_cents: 'Pix fixo',
  withdraw_fee_cents: 'Saque',
  daily_withdraw_limit_cents: 'Limite/dia',
  chargeback_fee_cents: 'Chargeback',
  reserve_pct_bps: 'Reserva',
  reserve_days: 'Retenção',
};

/** Valor de um campo da tabela já formatado para leitura. */
export function formatRate(key: RateKey, rate: RateSettings): string {
  if (key === 'pix_pct_bps' || key === 'reserve_pct_bps') {
    return formatBps(rate[key]);
  }
  if (key === 'reserve_days') {
    return `D+${rate.reserve_days}`;
  }
  if (key === 'daily_withdraw_limit_cents') {
    return dailyLimitLabel(rate.daily_withdraw_limit_cents);
  }
  return formatCents(rate[key]);
}

/**
 * Campos em que duas versões diferem. Uma linha `inherits_default` é a marcadora de reset
 * (`spec/08` §4.3): os campos dela não valem nada, quem passa a valer é o default da
 * plataforma -, então comparar campo a campo mostraria diferença inventada.
 */
export function rateDiff(current: RateSettings, other: RateSettings): RateKey[] {
  if (other.inherits_default) {
    return [];
  }
  return RATE_KEYS.filter((key) => current[key] !== other[key]);
}

export interface Simulation {
  /** Parte percentual da taxa. */
  pctFeeCents: number;
  /** `pctOf(bruto) + fixo`, a fórmula de `computeFees` (`packages/core/src/transactions/fees.ts`). */
  feeCents: number;
  /** Bruto − taxa. */
  netCents: number;
  /** `pctOf(líquido, reserve_pct_bps)`: a reserva incide sobre o líquido, não sobre o bruto. */
  reserveCents: number;
  /** O que cai no disponível na hora. */
  availableCents: number;
  /** Custo efetivo em basis points, por inteiro (nada de float até o rótulo). */
  effectiveBps: number;
}

/** A mesma conta do servidor quando o Pix é confirmado, feita no cliente sobre a taxa vigente. */
export function simulate(amountCents: number, rate: RateSettings): Simulation {
  const pctFeeCents = pctOf(amountCents, rate.pix_pct_bps);
  const feeCents = pctFeeCents + rate.pix_fixed_cents;
  const netCents = amountCents - feeCents;
  const reserveCents = pctOf(netCents, rate.reserve_pct_bps);
  return {
    pctFeeCents,
    feeCents,
    netCents,
    reserveCents,
    availableCents: netCents - reserveCents,
    effectiveBps: amountCents === 0 ? 0 : Math.round((feeCents * 10_000) / amountCents),
  };
}
